/**
 * baseQueryWithReauth — wraps the raw base query to add:
 *   - session upkeep, as legacy's interceptors do (services/sessionRefresh.js):
 *     before the request, an expired token is refreshed and a session ended
 *     elsewhere stops it; after it, a 401 ends the session.
 *   - Error normalization: always resolve to { status, message, data }.
 */
import { env } from '@config/env';
import { getAccessToken } from '@security/auth/tokenStore';

import { rawBaseQuery } from './apiClient';
import {
  SESSION_EXPIRED_MESSAGE,
  ensureFreshSession,
  expireSession,
  isSessionFreeUrl,
} from './sessionRefresh';

/** Per-status copy, ported from legacy `apiClient.errorHandler`. */
const STATUS_MESSAGE = {
  413: 'File size is too large.',
  502: 'The server is busy. Please try again.',
  TIMEOUT_ERROR: 'The request timed out. Please try again.',
  FETCH_ERROR: 'Server under maintenance. Please try again later.',
};

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Collapse any failure into { status, message, data }.
 *
 * Two quirks of the existing backend this has to absorb:
 *  - the message key is sometimes `Message` and sometimes `message`. Legacy
 *    reads `response.data.Message || response.data.message`; reading only one
 *    silently loses half the server's error text.
 *  - some 200 responses carry `ErrorCode: 1` with the real problem in the body.
 *    Those never reach here (they are not HTTP errors) — endpoints that can
 *    return them must check in their own transformResponse.
 *
 * Exported for tests; the base query attaches it as `error.normalized`.
 */
export function normalizeError(error) {
  const status = error?.status ?? 'FETCH_ERROR';
  const data = error?.data;

  const serverMessage = typeof data === 'string' ? data : (data?.Message ?? data?.message ?? '');

  return {
    status,
    message: serverMessage || STATUS_MESSAGE[status] || FALLBACK_MESSAGE,
    // Raw body in dev only: it can carry identifiers that should not reach a
    // production user's console.
    data: env.isDev ? data : undefined,
  };
}

/** What a request stopped by an ended session resolves to: a 401, sent nowhere. */
const SESSION_ENDED = Object.freeze({ status: 401, data: SESSION_EXPIRED_MESSAGE });

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  const sessionBound = !isSessionFreeUrl(args?.url);

  // `sessionEnded` on these errors: SessionNotice has already said why, so
  // useNotify().error stays quiet instead of repeating it per request.
  if (sessionBound && !(await ensureFreshSession(api.dispatch))) {
    return {
      error: { ...SESSION_ENDED, normalized: { ...normalizeError(SESSION_ENDED), sessionEnded: true } },
    };
  }

  // Captured before the call: a 401 ends the session only if there was one.
  const hadSession = Boolean(getAccessToken());
  const result = await rawBaseQuery(args, api, extraOptions);

  // Legacy: any 401 clears the session and leaves for the Okta logout, with the
  // server's message (apiservice.js:277-296). No retry — legacy refreshes only
  // ahead of a request, on the token's own expiry, never after a 401.
  // Only the server's own Message/message, as legacy; without one, expireSession
  // falls back to "Session expired. Please login again.".
  const endsSession = sessionBound && hadSession && result.error?.status === 401;
  if (endsSession) {
    const body = result.error.data;
    expireSession(api.dispatch, body?.Message || body?.message);
  }

  if (result.error) {
    result.error.normalized = normalizeError(result.error);
    if (endsSession) result.error.normalized.sessionEnded = true;
  }

  return result;
};
